import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { useProxyAccessControl } from '../../../../hooks/useProxyAccessControl';
import { useAdminPageAccessControl } from '../../../../hooks/useAdminPageAccessControl';
import {
  Box,
  Button,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormHelperText,
  Dialog,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import * as Sentry from '@sentry/react';

import {
  gray100,
  primaryButtonBackgroundColor,
  gray40,
  white,
  gray80,
  orange50,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../../constants/colors';
import { companyPlanChangeSchema } from '../../../../validation/company';
import type { CompanyPlanChangeFormType } from '../../../../types/company';
import { companyPlan, paymentCycle, paymentMethod } from '../../../../constants/enum';

import CustomTextfield from '../../../uiParts/form/CustomTextfield';
import CustomSnackbar from '../../../uiParts/form/CustomSnackbar';
import CustomLoading from '../../../uiParts/form/CustomLoading';
import MetaTitle from '../../../utils/MetaTitle';
import SideBar from '../../../uiParts/menu/SideBar';
import { usePageLayout } from '../../../../hooks/usePageLayout';
import { getCompany } from '../../../../api/firebase/firestore/company/read';
import { updateCompanyPlan } from '../../../../api/firebase/firestore/company/update';
import { boldMd, regularLg, regularXl } from '../../../../constants/location';
import { getSelectablePlans, getPlanLabel } from '../../../../constants/plans';
import { AdminNoPermissionPage } from '../../../uiParts/parts/AdminNoPermission';

const styles = {
  pageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
  },
  modalContainer: {
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    padding: '40px',
    width: '480px',
    boxSizing: 'border-box',
    position: 'relative',
    '@media screen and (max-width: 960px)': {
      padding: '32px 16px',
      width: '100%',
      maxWidth: '480px',
    },
  },
  modalHeader: {
    marginBottom: '24px',
    '@media screen and (max-width: 960px)': {
      marginBottom: '16px',
    },
  },
  modalTitle: {
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    textAlign: 'left',
    '@media screen and (max-width: 960px)': {
      fontSize: '20px',
    },
  },
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  backButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  backButtonText: {
    ...boldMd,
    color: gray80,
  },
  submitButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  submitButtonText: {
    ...boldMd,
    color: 'white',
  },
  formLabel: {
    fontSize: '14px',
    color: gray80,
  },
  required: {
    color: red50,
    fontSize: '12px',
    marginLeft: '8px',
  },
  radioLabel: {
    color: gray100,
    fontSize: '14px',
  },
  radio: {
    '&.Mui-checked': {
      color: orange50,
    },
  },
  dialogPaper: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      '@media screen and (max-width: 960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  dialogTitle: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  dialogIcon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  dialogIconButton: {
    padding: 0,
  },
  dialogButtonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  dialogCancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  dialogConfirmButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '24px',
  },
  errorText: {
    fontSize: '16px',
    color: gray100,
    textAlign: 'center',
  },
};

const CompanyPlanChange: React.FC = (): JSX.Element => {
  const { companyId } = useParams<{ companyId: string }>();
  const [errorText, setErrorText] = useState('');
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<number | null>(null);
  const [currentPaymentCycle, setCurrentPaymentCycle] = useState<number | null>(null);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<number | null>(null);
  const [companyNotFound, setCompanyNotFound] = useState(false);
  const { user, noPermission, isChecking } = useAdminPageAccessControl('plan change page');
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  // 代理モード中はアクセス禁止
  useProxyAccessControl();

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CompanyPlanChangeFormType>({
    resolver: yupResolver(companyPlanChangeSchema),
    defaultValues: {
      plan: 0,
      paymentCycle: undefined,
      paymentMethodValue: undefined,
      note: '',
    },
  });

  const selectedPlan = watch('plan');
  const selectedPaymentCycle = watch('paymentCycle');
  const selectedPaymentMethod = watch('paymentMethodValue');

  // 有料プランかどうか
  const isPaidPlan = selectedPlan !== companyPlan.free;

  const isButtonDisabled = (): boolean => {
    if (currentPlan === null) return true;
    // プラン・支払いサイクル・支払い方法すべて変更なしの場合は無効
    if (
      selectedPlan === currentPlan &&
      selectedPaymentCycle === currentPaymentCycle &&
      selectedPaymentMethod === currentPaymentMethod
    )
      return true;
    // 有料プランで支払いサイクルまたは支払い方法が未選択の場合は無効
    if (isPaidPlan && (!selectedPaymentCycle || !selectedPaymentMethod)) return true;
    return false;
  };

  const handleBack = (): void => {
    navigate(-1);
  };

  const handleConfirmClick = async (): Promise<void> => {
    await handleSubmit(() => {
      setDialogOpen(true);
    })();
  };

  const handleDialogClose = (): void => {
    setDialogOpen(false);
  };

  const handlePlanChange = async (): Promise<void> => {
    if (!companyId || !user?.uid || currentPlan === null) return;

    try {
      setSubmitting(true);
      const note = watch('note') ?? '';
      const cycleValue = isPaidPlan ? selectedPaymentCycle : paymentCycle.none;
      const methodValue = isPaidPlan ? selectedPaymentMethod : undefined;

      await updateCompanyPlan(
        companyId,
        currentPlan,
        selectedPlan,
        note as string,
        user.uid,
        cycleValue,
        methodValue,
      );

      navigate(`/admin/plan_change/${companyId}/complete`, {
        state: { fromPlanChange: true },
      });
    } catch (error: unknown) {
      Sentry.captureException(error);
      setErrorText('プラン変更に失敗しました。時間をおいて再度お試しください。');
      setOpen(true);
      setDialogOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchCompanyData = async (): Promise<void> => {
      // アクセス制御はフックに委譲
      if (isChecking || noPermission || !user) return;

      if (!companyId || dataFetched) return;

      try {
        const companyData = await getCompany(companyId);

        if (!companyData) {
          Sentry.captureMessage('Company not found for plan change', {
            level: 'warning',
            extra: { companyId },
          });
          setCompanyNotFound(true);
          setLoading(false);
          return;
        }

        const plan = companyData.plan as number;
        const cycle = (companyData.payment_cycle as number) ?? paymentCycle.none;
        const method = (companyData.payment_method as number) ?? 0;
        setCurrentPlan(plan);
        setCurrentPaymentCycle(cycle);
        setCurrentPaymentMethod(method);
        setValue('plan', plan);
        if (plan !== companyPlan.free) {
          setValue('paymentCycle', cycle);
          if (method > 0) {
            setValue('paymentMethodValue', method);
          }
        }
        setDataFetched(true);
      } catch (error: unknown) {
        Sentry.captureException(error);
        setErrorText('企業情報の取得に失敗しました。');
        setOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [isChecking, noPermission, user, navigate, setValue, dataFetched, companyId]);

  // 権限がない場合
  if (noPermission) {
    return <AdminNoPermissionPage companyId={companyId} />;
  }

  // ローディング中はフォームを描画しない
  if (isChecking || loading) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box
          sx={[
            layoutStyles.pageContent,
            sideBarCollapsed && layoutStyles.pageContentCollapsed,
            layoutStyles.pageContentNoFooter,
          ]}
        >
          <CustomLoading loading={true} />
        </Box>
      </Box>
    );
  }

  // 企業が存在しない場合
  if (companyNotFound) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box
          sx={[
            layoutStyles.pageContent,
            sideBarCollapsed && layoutStyles.pageContentCollapsed,
            layoutStyles.pageContentNoFooter,
          ]}
        >
          <Box sx={[styles.pageContainer, layoutStyles.fullHeightContent]}>
            <Box sx={styles.modalContainer}>
              <Box sx={styles.errorContainer}>
                <Typography sx={styles.errorText}>
                  指定された企業が見つかりませんでした。
                </Typography>
                <Button
                  onClick={(): void => navigate('/admin/company')}
                  sx={[primaryButtonBackgroundColor, { width: '200px', height: '44px' }]}
                  variant="contained"
                >
                  企業一覧に戻る
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          layoutStyles.pageContentNoFooter,
        ]}
      >
        <Box sx={[styles.pageContainer, layoutStyles.fullHeightContent]}>
          <Box sx={styles.modalContainer}>
            <Box sx={styles.modalHeader}>
              <Typography sx={styles.modalTitle}>契約プラン変更</Typography>
            </Box>

            <FormControl component="fieldset" sx={{ width: '100%', marginBottom: '24px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <Typography sx={styles.formLabel}>契約プランの種類</Typography>
                <Typography sx={styles.required}>必須</Typography>
              </Box>
              <Controller
                name="plan"
                control={control}
                render={({ field }): JSX.Element => (
                  <RadioGroup
                    {...field}
                    value={field.value}
                    onChange={(e): void => {
                      const newPlan = Number(e.target.value);
                      field.onChange(newPlan);
                      // フリープラン選択時は支払いサイクル・支払い方法をクリア
                      if (newPlan === companyPlan.free) {
                        setValue('paymentCycle', undefined);
                        setValue('paymentMethodValue', undefined);
                      }
                    }}
                  >
                    {getSelectablePlans().map(plan => (
                      <FormControlLabel
                        key={plan.planCode}
                        value={plan.planCode}
                        control={<Radio sx={styles.radio} />}
                        label={<Typography sx={styles.radioLabel}>{plan.name}</Typography>}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
              {errors.plan && <FormHelperText error>{errors.plan.message}</FormHelperText>}
            </FormControl>

            {isPaidPlan && (
              <FormControl component="fieldset" sx={{ width: '100%', marginBottom: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <Typography sx={styles.formLabel}>支払いサイクル</Typography>
                  <Typography sx={styles.required}>必須</Typography>
                </Box>
                <Controller
                  name="paymentCycle"
                  control={control}
                  render={({ field }): JSX.Element => (
                    <RadioGroup
                      {...field}
                      row
                      value={field.value ?? ''}
                      onChange={(e): void => field.onChange(Number(e.target.value))}
                    >
                      <FormControlLabel
                        value={paymentCycle.monthly}
                        control={<Radio sx={styles.radio} />}
                        label={<Typography sx={styles.radioLabel}>月払い</Typography>}
                      />
                      <FormControlLabel
                        value={paymentCycle.yearly}
                        control={<Radio sx={styles.radio} />}
                        label={<Typography sx={styles.radioLabel}>年払い</Typography>}
                      />
                    </RadioGroup>
                  )}
                />
                {errors.paymentCycle && (
                  <FormHelperText error>{errors.paymentCycle.message}</FormHelperText>
                )}
              </FormControl>
            )}

            {isPaidPlan && (
              <FormControl component="fieldset" sx={{ width: '100%', marginBottom: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <Typography sx={styles.formLabel}>支払い方法</Typography>
                  <Typography sx={styles.required}>必須</Typography>
                </Box>
                <Controller
                  name="paymentMethodValue"
                  control={control}
                  render={({ field }): JSX.Element => (
                    <RadioGroup
                      {...field}
                      row
                      value={field.value ?? ''}
                      onChange={(e): void => field.onChange(Number(e.target.value))}
                    >
                      <FormControlLabel
                        value={paymentMethod.invoice}
                        control={<Radio sx={styles.radio} />}
                        label={<Typography sx={styles.radioLabel}>請求書</Typography>}
                      />
                      <FormControlLabel
                        value={paymentMethod.creditCard}
                        control={<Radio sx={styles.radio} />}
                        label={<Typography sx={styles.radioLabel}>クレジットカード</Typography>}
                      />
                    </RadioGroup>
                  )}
                />
                {errors.paymentMethodValue && (
                  <FormHelperText error>{errors.paymentMethodValue.message}</FormHelperText>
                )}
              </FormControl>
            )}

            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.note)}
                  formMb="0px"
                  helperText={errors.note?.message}
                  labelText="備考"
                  placeholder=""
                  width="100%"
                  inputWidth="100%"
                  multiline
                  rows={4}
                  height="auto"
                />
              )}
              control={control}
              name="note"
            />

            <Box sx={styles.buttonWrapper}>
              <Button sx={styles.backButton} onClick={handleBack}>
                <Typography sx={styles.backButtonText}>戻る</Typography>
              </Button>
              <Button
                disabled={isButtonDisabled()}
                onClick={handleConfirmClick}
                sx={styles.submitButton}
                variant="contained"
              >
                <Typography sx={styles.submitButtonText}>確認</Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 確認ダイアログ */}
      <Dialog open={dialogOpen} sx={styles.dialogPaper}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitle}>契約プラン変更確認</Typography>
          <IconButton onClick={handleDialogClose} sx={styles.dialogIconButton}>
            <Close sx={styles.dialogIcon} />
          </IconButton>
        </Box>
        <Typography>
          現在のプラン（{getPlanLabel(currentPlan ?? 0)}）から{getPlanLabel(selectedPlan)}
          {isPaidPlan &&
            selectedPaymentCycle &&
            `（${selectedPaymentCycle === paymentCycle.monthly ? '月払い' : '年払い'}・${
              selectedPaymentMethod === paymentMethod.creditCard ? 'クレジットカード' : '請求書'
            }）`}
          に変更します。
          <br />
          実行してもよろしいですか？
        </Typography>
        <Box sx={styles.dialogButtonWrapper}>
          <Button sx={styles.dialogCancelButton} onClick={handleDialogClose} disabled={submitting}>
            <Typography sx={styles.backButtonText}>キャンセル</Typography>
          </Button>
          <Button
            sx={styles.dialogConfirmButton}
            onClick={handlePlanChange}
            disabled={submitting}
            variant="contained"
          >
            <Typography sx={styles.submitButtonText}>
              {submitting ? '変更中...' : '変更する'}
            </Typography>
          </Button>
        </Box>
      </Dialog>

      <CustomSnackbar open={open} onClose={(): void => setOpen(false)} text={errorText} />
    </Box>
  );
};

export default CompanyPlanChange;
